import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Badge, 
  IconButton, 
  Box, 
  Container,
  InputBase,
  alpha,
  styled,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemText,
  ListItemIcon,
  Paper,
  ButtonGroup,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  Avatar,
  Stack,
  Grid as MuiGrid
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CategoryIcon from '@mui/icons-material/Category';
import HomeIcon from '@mui/icons-material/Home';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupIcon from '@mui/icons-material/Group';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useCart } from '../context/CartContext';
import { StoreContext } from '../App';
import { useQuery } from '@tanstack/react-query';
import { categoriesApi } from '../api/api';
import { useAuth } from '../context/AuthContext';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  border: '1px solid',
  borderColor: theme.palette.divider,
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));

const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

const Header: React.FC = () => {
  const { itemCount } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const storeContext = useContext(StoreContext);
  const [scrolled, setScrolled] = useState(false);

  // Mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Category menu states
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<null | number>(null);
  
  // State for mobile category accordion
  const [openCategories, setOpenCategories] = useState<number[]>([]); 

  // Auth menu
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  
  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.getCategories,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Handle scroll event to add elevation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Extract search query from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (query) {
      setSearchTerm(query);
    } else {
      setSearchTerm(''); // Clear search term if no query param
    }
  }, [location.search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      if (mobileOpen) setMobileOpen(false);
    }
  };

  // Category menu handlers
  const handleCategoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCategoryAnchorEl(event.currentTarget);
    setCategoryMenuOpen(true);
  };

  const handleCategoryMenuClose = () => {
    setCategoryMenuOpen(false);
    setExpandedCategoryId(null);
  };
  
  const handleCategoryToggle = (event: React.MouseEvent, categoryId: number) => {
    event.stopPropagation();
    setExpandedCategoryId(prevId => prevId === categoryId ? null : categoryId);
  };

  // Handles clicking on a subcategory item
  const handleSubCategoryClick = (event: React.MouseEvent, subCategoryId: number) => {
    event.stopPropagation(); // Prevent parent handlers
    navigate(`/category/${subCategoryId}`);
    handleCategoryMenuClose(); // Close desktop menu if open
    if (mobileOpen) setMobileOpen(false); // Close mobile drawer
  };

  // Get parent categories (top level)
  const parentCategories = React.useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData.items.filter(cat => !cat.parentId);
  }, [categoriesData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleUserMenuClose();
    if (mobileOpen) setMobileOpen(false);
  };
  
  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handle category toggle specifically for the mobile drawer accordion
  const handleMobileCategoryToggle = (categoryId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent parent click handlers
      e.preventDefault();
    }
    setOpenCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId) 
        : [...prev, categoryId]
    );
  };

  // Mobile Drawer Content - Performance optimized with fixed height containers and proper event handling
  const drawer = (
    <Box sx={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ my: 1.5 }}>
          {storeContext.name}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch' // For smooth scrolling on iOS
      }}>
        <List component="nav">
          <ListItemButton onClick={() => handleNavigate('/')}>
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
          
          <ListItemButton onClick={() => handleNavigate('/categories')}>
            <ListItemIcon><CategoryIcon /></ListItemIcon>
            <ListItemText primary="All Categories" />
          </ListItemButton>
          
          <ListItemButton 
            onClick={(e) => handleMobileCategoryToggle(0, e)}
            sx={{ position: 'relative' }}
          >
            <ListItemIcon><CategoryIcon /></ListItemIcon>
            <ListItemText primary="Browse Categories" />
            {openCategories.includes(0) ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          
          {/* Category list with fixed height and overflow */}
          <Collapse 
            in={openCategories.includes(0)} 
            timeout="auto" 
            sx={{ 
              maxHeight: '40vh', 
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative'
            }}
          >
            <List component="div" disablePadding>
              {parentCategories.map((category) => {
                const hasSubcategories = categoriesData?.items.some(c => c.parentId === category.id);
                return (
                  <React.Fragment key={category.id}>
                    <ListItemButton 
                      sx={{ pl: 4 }}
                      onClick={() => handleNavigate(`/category/${category.id}`)} 
                    >
                      <ListItemText primary={category.name} />
                      {hasSubcategories && (
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            handleMobileCategoryToggle(category.id, e);
                          }}
                          sx={{ 
                            position: 'absolute',
                            right: 8
                          }}
                        >
                          {openCategories.includes(category.id) ? 
                            <ExpandLess fontSize="small" /> : 
                            <ExpandMore fontSize="small" />}
                        </IconButton>
                      )}
                    </ListItemButton>
                    
                    {hasSubcategories && (
                      <Collapse 
                        in={openCategories.includes(category.id)} 
                        timeout="auto"
                      >
                        <List component="div" disablePadding>
                          {categoriesData?.items
                            .filter(c => c.parentId === category.id)
                            .map(subCategory => (
                              <ListItemButton
                                key={subCategory.id}
                                sx={{ pl: 8 }}
                                onClick={() => handleNavigate(`/category/${subCategory.id}`)}
                              >
                                <ListItemText 
                                  primary={subCategory.name} 
                                  primaryTypographyProps={{ 
                                    variant: 'body2',
                                    sx: { fontSize: '0.875rem' } 
                                  }} 
                                />
                              </ListItemButton>
                            ))
                          }
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Collapse>
          
          <Divider sx={{ my: 1 }} />
          
          {isAuthenticated ? (
            <>
              <ListItemButton onClick={() => handleNavigate('/profile')}>
                <ListItemIcon><AccountCircleIcon /></ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItemButton>
              <ListItemButton onClick={() => handleNavigate('/favorites')}>
                <ListItemIcon><FavoriteBorderIcon /></ListItemIcon>
                <ListItemText primary="My Favorites" />
              </ListItemButton>
              <ListItemButton onClick={() => handleNavigate('/orders')}>
                <ListItemIcon><ShoppingCartIcon /></ListItemIcon>
                <ListItemText primary="My Orders" />
              </ListItemButton>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </>
          ) : (
            <ListItemButton onClick={() => handleNavigate('/login')}>
              <ListItemIcon><LoginIcon /></ListItemIcon>
              <ListItemText primary="Login / Register" />
            </ListItemButton>
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          bgcolor: '#ffffff',
          boxShadow: scrolled ? '0 4px 12px rgba(0, 0, 0, 0.08)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
          transition: 'box-shadow 0.3s ease',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: 'text.primary'
        }}
        elevation={0}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
            {/* Mobile Menu Icon */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Logo */}
            <Box 
              component={Link}
              to="/"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0, 
                mr: { xs: 1, sm: 2, md: 3 },
                height: { xs: 35, sm: 40, md: 45 }
              }}
            >
              <img src="/logo.png" alt={`${storeContext.name} Logo`} style={{ height: '100%', width: 'auto' }} />
            </Box>

            {/* Desktop Navigation Links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              <Button 
                onClick={handleCategoryMenuOpen}
                color="inherit"
                endIcon={<ExpandMore />}
                aria-controls={categoryMenuOpen ? 'category-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={categoryMenuOpen ? 'true' : undefined}
              >
                Categories
              </Button>
              {/* Category Dropdown Menu */}
              <Menu
                id="category-menu"
                anchorEl={categoryAnchorEl}
                open={categoryMenuOpen}
                onClose={handleCategoryMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'category-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                sx={{ 
                  '& .MuiList-root': { pt: 0 },
                  '& .MuiPaper-root': { 
                    maxHeight: '70vh',
                    width: 280,
                    overflowY: 'auto'
                  }
                }}
              >
                <MenuItem 
                  onClick={() => { handleNavigate('/categories'); handleCategoryMenuClose(); }}
                  sx={{ fontWeight: 'bold', pt: 1, pb: 1 }}
                >
                  <ListItemText>View All Categories</ListItemText>
                </MenuItem>
                <Divider sx={{ mb: 0 }}/>
                {parentCategories.map((category) => {
                  const hasSubcategories = categoriesData?.items.some(c => c.parentId === category.id);
                  const isExpanded = expandedCategoryId === category.id;
                  
                  return (
                    <React.Fragment key={category.id}>
                      <MenuItem 
                        onClick={() => {
                          navigate(`/category/${category.id}`);
                          handleCategoryMenuClose();
                        }}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          pr: 1,
                          position: 'relative'  // Added for positioning child elements
                        }}
                      >
                        <ListItemText sx={{ mr: 1 }}>{category.name}</ListItemText>
                        {hasSubcategories && (
                          <IconButton 
                            size="small"
                            onClick={(e) => handleCategoryToggle(e, category.id)}
                            aria-label={isExpanded ? 'Collapse category' : 'Expand category'}
                            sx={{ 
                              position: 'absolute',
                              right: 8
                            }}
                          >
                            {isExpanded ? <RemoveCircleOutlineIcon fontSize="small" /> : <AddCircleOutlineIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </MenuItem>
                      {hasSubcategories && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding sx={{ pl: 2 }}>
                            {categoriesData?.items
                              .filter(c => c.parentId === category.id)
                              .map(subCategory => (
                                <ListItemButton
                                  key={subCategory.id}
                                  sx={{ pl: 2 }}
                                  onClick={(e) => handleSubCategoryClick(e, subCategory.id)}
                                >
                                  <ListItemText primary={subCategory.name} primaryTypographyProps={{ variant: 'body2' }} />
                                </ListItemButton>
                              ))}
                          </List>
                        </Collapse>
                      )}
                      {parentCategories.indexOf(category) < parentCategories.length - 1 && <Divider />} 
                    </React.Fragment>
                  );
                })}
              </Menu>
            </Box>
            
            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Right side elements (Email, Phone, Icons) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
              {/* Contact Info (Desktop) */}
              <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2, mr: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  Orders@NaturalSourceSnacks.ca
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  604 243 8700
                </Typography>
              </Box>
              
              {/* Cart Icon */}
              <Tooltip title="Shopping Cart">
                <IconButton component={Link} to="/cart" color="inherit">
                  <Badge badgeContent={itemCount} color="secondary">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* User/Login Icon */}
              {isAuthenticated ? (
                <Tooltip title="Account">
                  <IconButton onClick={handleUserMenuOpen} color="inherit" size="medium">
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>
                      <PersonOutlineIcon fontSize="medium" />
                    </Avatar>
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Login / Register">
                  <IconButton component={Link} to="/login" color="inherit">
                    <PersonOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* User Dropdown Menu */}
              <Menu
                anchorEl={userMenuAnchorEl}
                open={userMenuOpen}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={() => handleNavigate('/profile')}>
                  <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                  My Profile
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/favorites')}>
                  <ListItemIcon><FavoriteBorderIcon fontSize="small" /></ListItemIcon>
                  My Favorites
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/orders')}>
                  <ListItemIcon><ShoppingCartIcon fontSize="small" /></ListItemIcon>
                  My Orders
                </MenuItem>
                {user?.role === 'admin' && (
                  <MenuItem onClick={() => handleNavigate('/admin/users')}>
                    <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
                    User Management
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Secondary Info Bar */}
      <Paper 
        elevation={0} 
        sx={{ 
          py: 2, 
          px: { xs: 1, sm: 2 },
          bgcolor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            {/* Delivery Times - Simplified structure */}
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: (theme: Theme) => theme.spacing(1.5), minWidth: 0 }}> 
              <ShoppingCartIcon color="action" sx={{ fontSize: { xs: 26, sm: 28 }, flexShrink: 0 }}/> 
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" lineHeight={1.3}>
                  ORDER BY: <Typography component="span" fontWeight="bold">10am Friday</Typography> for Mon / Tues Delivery
                </Typography>
                <Typography variant="caption" display="block" lineHeight={1.3}>
                  ORDER BY: <Typography component="span" fontWeight="bold">10am Tuesday</Typography> for Wed / Thurs Delivery
                </Typography>
              </Box>
            </Grid>
            
            {/* Free Delivery Info - Simplified structure */}
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: (theme: Theme) => theme.spacing(1.5), minWidth: 0 }}> 
              <LocalShippingIcon color="action" sx={{ fontSize: { xs: 26, sm: 28 }, flexShrink: 0 }}/> 
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" display="block">
                  ORDER $99+ <Typography component="span" color="primary.main">FREE DELIVERY</Typography>
                </Typography>
                <Typography variant="caption" display="block" lineHeight={1.3}>
                  *$9.99 fee for orders less than $99
                </Typography>
              </Box>
            </Grid>

            {/* Search Bar - Expands to fill remaining space */}
            <Grid item xs={12} md sx={{ flexGrow: 1, minWidth: 0 }}> 
              <Box component="form" onSubmit={handleSearchSubmit} sx={{ width: '100%' }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder="Search products…"
                    inputProps={{ 'aria-label': 'search' }}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    fullWidth
                  />
                </Search>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Paper>
      
      {/* Mobile Drawer - Fixed position */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileMenuToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            height: '100%',
            overflowY: 'hidden' // Prevent double scrollbars
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header; 